/**
 * Signing in, from the application's point of view.
 *
 * The flow itself is in @plan-forge/entra-auth, ported from tools/sso-check where it was
 * validated live with IT. This file is configuration and the one hook the API client needs.
 *
 * Auth is optional at build time, and deliberately visible when it is off. An unconfigured
 * build runs against an open API, which is a real state this environment has been in - so the
 * interface says so rather than looking signed-in.
 */

import {
  beginSignIn,
  completeSignIn,
  restoreSession,
  signOut as clearSession,
  tokenFor,
  userFrom,
  type EntraConfig,
  type Session,
  type SignedInUser,
} from "@plan-forge/entra-auth";

function readConfig(): EntraConfig | null {
  const tenantId = import.meta.env["VITE_ENTRA_TENANT_ID"] ?? "";
  const clientId = import.meta.env["VITE_ENTRA_CLIENT_ID"] ?? "";
  const applicationIdUri = import.meta.env["VITE_ENTRA_APP_ID_URI"] ?? "";
  if (tenantId === "" || clientId === "" || applicationIdUri === "") return null;
  return {
    tenantId,
    clientId,
    applicationIdUri,
    // Derived rather than configured. It has to match a URI registered on the app, and the
    // registration names this exact path for both localhost and the deployed origin - so
    // deriving it from wherever the app is actually served removes one thing to keep in step.
    redirectUri: `${window.location.origin}/auth/callback`,
  };
}

export const entraConfig = readConfig();
export const authRequired = entraConfig !== null;

export type AuthState =
  | { readonly kind: "disabled" }
  | { readonly kind: "loading" }
  | { readonly kind: "signed-out"; readonly reason?: string }
  | { readonly kind: "signed-in"; readonly session: Session; readonly user: SignedInUser };

/**
 * Works out where the application stands on load: returning from Entra, resumable from a
 * refresh token in this tab, or signed out.
 */
export async function initialAuthState(): Promise<AuthState> {
  if (entraConfig === null) return { kind: "disabled" };

  try {
    const redirect = await completeSignIn(entraConfig);
    if (redirect !== null) {
      // Take the code out of the address bar before anything else can copy it or a reload can
      // replay it. replaceState rather than assign, so this is not a second navigation.
      window.history.replaceState({}, "", redirect.returnTo);
      return { kind: "signed-in", session: redirect.session, user: userFrom(redirect.session) };
    }
  } catch (error) {
    return { kind: "signed-out", reason: (error as Error).message };
  }

  const restored = await restoreSession(entraConfig);
  return restored === null
    ? { kind: "signed-out" }
    : { kind: "signed-in", session: restored, user: userFrom(restored) };
}

export async function signIn(): Promise<void> {
  if (entraConfig === null) return;
  await beginSignIn(entraConfig);
}

export function signOut(): void {
  clearSession();
}

/**
 * The Authorization header for an API call, refreshing the token first if it is close to
 * expiring. Returns no header at all when auth is off, which is what lets the same client work
 * against an open dev API.
 *
 * `onRenewed` hands back the refreshed session so the application can hold the newer one;
 * without it every call after a refresh would keep presenting the old token and refresh again.
 */
export async function authHeader(
  state: AuthState,
  onRenewed: (session: Session) => void,
): Promise<Record<string, string>> {
  if (entraConfig === null || state.kind !== "signed-in") return {};
  const { token, session } = await tokenFor(entraConfig, state.session);
  if (session !== state.session) onRenewed(session);
  return { authorization: `Bearer ${token}` };
}
